import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Star, ShieldCheck, ArrowRight } from 'lucide-react';
import { api } from '../../lib/api';

/**
 * Sidebar/footer block that surfaces 6 other vendors the visitor may also
 * want to consider. Selection logic:
 *   1. First preference: vendors sharing at least one category with `current`
 *      (cheap match on `current.categories[*]`)
 *   2. Fallback: same city
 *   3. Final fallback: any other active vendor
 *
 * Server filtering would be more efficient long-term but our `/api/suppliers`
 * endpoint doesn't accept multi-filter yet, so we pull a small page (200) once
 * and slice on the client.
 */
const RelatedVendors = ({ current, limit = 6 }) => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.getSuppliers({ limit: 200 })
      .then((rows) => {
        if (!alive) return;
        setAll(Array.isArray(rows) ? rows : []);
      })
      .catch(() => setAll([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [current?.id]);

  if (loading || !all.length) return null;

  const cats = new Set((current?.categories || []).map((c) => String(c).toLowerCase()));
  const city = String(current?.city || '').toLowerCase().split(',')[0].trim();
  const others = all.filter((v) => v.id !== current?.id && v.status !== 'inactive');

  const score = (v) => {
    let s = 0;
    const vc = String(v.city || '').toLowerCase().split(',')[0].trim();
    if (vc && vc === city) s += 2;
    const overlap = (v.categories || []).reduce(
      (acc, c) => acc + (cats.has(String(c).toLowerCase()) ? 1 : 0),
      0,
    );
    s += overlap * 3;
    if (v.plan_name === 'Platinum') s += 1;
    if (v.plan_name === 'Gold') s += 0.5;
    s += Math.min(2, Number(v.rating || 0) / 3);
    return s;
  };

  const ranked = others
    .map((v) => ({ v, s: score(v) }))
    .sort((a, b) => b.s - a.s || (b.v.rating || 0) - (a.v.rating || 0))
    .slice(0, limit)
    .map(({ v }) => v);

  if (!ranked.length) return null;

  return (
    <section className="mt-8" data-testid="related-vendors-section">
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Related Vendors</h2>
          <p className="text-xs text-gray-500">Other businesses you may want to compare</p>
        </div>
        <Link to="/directory" className="text-xs font-semibold text-[#2c54b2] hover:text-[#ee5e23] inline-flex items-center gap-1">
          Browse all <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {ranked.map((v) => (
          <Link
            key={v.id}
            to={`/vendor/${v.slug || v.id}`}
            className="group bg-white border border-gray-200 rounded-md p-3 flex gap-3 hover:border-[#2c54b2] hover:shadow-md transition-all"
            data-testid={`related-vendor-${v.id}`}
          >
            {v.logo_url ? (
              <img src={v.logo_url} alt={v.name} className="w-12 h-12 rounded object-cover border bg-white shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded bg-gradient-to-br from-[#2c54b2] to-[#1e3a8a] text-white flex items-center justify-center text-sm font-bold shrink-0">
                {v.logo || (v.name || '').slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-sm text-gray-900 truncate group-hover:text-[#2c54b2]">{v.name}</div>
              <div className="flex items-center gap-2 text-[11px] text-gray-500 mt-0.5">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{v.city || '—'}</span>
              </div>
              <div className="flex items-center gap-2 mt-1.5 text-[11px]">
                {v.rating ? (
                  <span className="inline-flex items-center gap-0.5 text-amber-600 font-medium">
                    <Star className="w-3 h-3 fill-amber-500 text-amber-500" /> {Number(v.rating).toFixed(1)}
                  </span>
                ) : null}
                {v.gst && (
                  <span className="inline-flex items-center gap-0.5 text-emerald-700">
                    <ShieldCheck className="w-3 h-3" /> GST
                  </span>
                )}
                {v.claimed && (
                  <span className="text-[10px] uppercase font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">Verified</span>
                )}
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
};

export default RelatedVendors;
